import { useState } from "react";
import { apiFetch } from "../../../context/AuthContext";
import { API_URL } from "../config";
import { invalidateEmployeeCache } from "../utils/employeeCache";
import { DOCUMENT_CHECKLIST_FIELDS } from "../constants/documentChecklistFields";

// Same field list the view/documents modals use — keeps every screen in
// sync instead of drifting (this hook previously hardcoded its own 7-field
// subset of the real 14-field checklist).
const defaultDocumentChecklist = () =>
  DOCUMENT_CHECKLIST_FIELDS.reduce((acc, field) => {
    acc[field.name] = false;
    return acc;
  }, {});

// Documents checklist modal state + handlers.
//
// Ported from the admin Employee Management page's useEmployeeDocuments
// hook: in addition to the checklist, this now also fetches the employee's
// actual uploaded documents (GET /api/documents/:infinityCode) so
// DocumentsModal can show real files with preview/download, not just the
// checklist. The exported function/prop names are unchanged from before so
// EmployeeManagementManager.jsx's wiring didn't need to change.
export const useDocumentChecklist = ({ language, text, setEmployees, setError }) => {
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [selectedEmployeeDocuments, setSelectedEmployeeDocuments] = useState(null);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [savingDocument, setSavingDocument] = useState(false); // Track if saving is in progress
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false); // Track if there are unsaved changes
  const [documentChecklist, setDocumentChecklist] = useState(defaultDocumentChecklist);

  const handleViewDocuments = async (employee) => {
    try {
      setLoadingDocuments(true);
      setSelectedEmployeeDocuments(employee);
      setShowDocumentsModal(true);
      setHasUnsavedChanges(false); // Reset unsaved changes flag

      // Initialize document checklist from employee data (all 14 fields).
      const empData = employee.fullEmployeeData || {};
      const checklistData = DOCUMENT_CHECKLIST_FIELDS.reduce((acc, field) => {
        const raw = empData[field.name];
        // photosCount is stored as a count (e.g. 1), not a strict boolean —
        // every other field is a real boolean column.
        acc[field.name] =
          field.name === "photosCount" ? raw === 1 || raw === true : Boolean(raw);
        return acc;
      }, {});
      setDocumentChecklist(checklistData);

      // Fetch the employee's real uploaded documents (use infinityCode, not
      // the row's local `id`) so the modal can show them with preview/
      // download, matching the admin page's Documents modal.
      const infinityCode =
        employee.infinityCode || employee.fullEmployeeData?.infinityCode || employee.id;
      const response = await apiFetch(`${API_URL}/api/documents/${infinityCode}`, {
        method: "GET",
      });
      const responseData = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(responseData?.error || responseData?.message || "Failed to fetch documents");
      }

      setSelectedEmployeeDocuments({
        ...employee,
        documents: Array.isArray(responseData) ? responseData : [],
      });
    } catch (err) {
      console.error("❌ Failed to load documents:", err);
      // The checklist itself already loaded successfully above — only the
      // real-documents fetch failed, so keep the modal usable with an empty
      // documents list rather than surfacing a page-level error.
      setSelectedEmployeeDocuments((prev) => (prev ? { ...prev, documents: [] } : prev));
    } finally {
      setLoadingDocuments(false);
    }
  };

  // Handler to update document checklist
  // Update checkbox state without saving
  const handleDocumentCheckboxChange = (documentField, value) => {
    setDocumentChecklist(prev => ({
      ...prev,
      [documentField]: value
    }));
    setHasUnsavedChanges(true);
  };

  // Save all document changes to database
  const handleSaveDocuments = async () => {
    try {
      setSavingDocument(true);

      const payload = DOCUMENT_CHECKLIST_FIELDS.reduce((acc, field) => {
        acc[field.name] = Boolean(documentChecklist[field.name]);
        return acc;
      }, {});

      const response = await apiFetch(
        `${API_URL}/employees/${selectedEmployeeDocuments.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      const responseData = await response.json().catch(() => null);

      if (!response.ok) {
        const errorMessage = responseData?.message || responseData?.error || `Server error: ${response.status}`;
        throw new Error(errorMessage);
      }

      // Update the employee data in state
      setEmployees(prev => prev.map(emp =>
        emp.id === selectedEmployeeDocuments.id
          ? { ...emp, fullEmployeeData: { ...emp.fullEmployeeData, ...payload } }
          : emp
      ));

      // The cached raw employee/hierarchy payload no longer reflects this
      // document update — drop it so a remount within the TTL window (or
      // the next hierarchy expand/collapse) doesn't revert it.
      invalidateEmployeeCache("manager-employees:");

      // Keep the modal's own copy of the employee in sync too.
      setSelectedEmployeeDocuments((prev) =>
        prev ? { ...prev, fullEmployeeData: { ...prev.fullEmployeeData, ...payload } } : prev
      );

      // Clear unsaved changes flag
      setHasUnsavedChanges(false);
      setSavingDocument(false);

    } catch (err) {
      console.error("❌ Failed to save documents:", err);
      setSavingDocument(false);
      setError(`${text[language].failedToUpdateDocuments}: ${err.message}`);
    }
  };

  return {
    showDocumentsModal,
    setShowDocumentsModal,
    selectedEmployeeDocuments,
    setSelectedEmployeeDocuments,
    setHasUnsavedChanges,
    loadingDocuments,
    savingDocument,
    hasUnsavedChanges,
    documentChecklist,
    setDocumentChecklist,
    handleViewDocuments,
    handleDocumentCheckboxChange,
    handleSaveDocuments,
  };
};
